import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { referentielSocietes as api } from '../services/api';
import './Objectifs.css';
import './Stock.css';
import './ReferentielVentesAdd.css';   // ob-ok
import './Creances.css';               // cr-btn
import './AdminSocietes.css';

// ─── Administration ▸ Sociétés (2026-10-29) ─────────────────────────────────
// Les deux axes de regroupement des écrans, libellés libres : la concession
// groupée (plusieurs sociétés, un même site ou une même direction) et la
// plaque (pôle commercial : ASIE, ALLEMAGNE…). Ils alimentent le panneau de
// filtres de tous les rapports et le sélecteur de la barre latérale. Saisis
// jusqu'ici à la main en base ; désormais ici, par un administrateur, chaque
// modification historisée. Les valeurs déjà utilisées sont proposées à la
// saisie pour éviter « Asie » à côté de « ASIE ».

const date = (d) => new Date(d).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
const val = (v) => v || <span className="as-vide">aucune</span>;

export default function AdminSocietes() {
  const [params, setParams] = useSearchParams();
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [brouillons, setBrouillons] = useState({});   // code → { master, plaque }
  const [attente, setAttente] = useState(null);       // code en cours d'enregistrement

  const charger = useCallback(() => api.charger()
    .then((r) => setD(r.data))
    .catch((e) => setErreur(e.response?.data?.error || 'Erreur de chargement')), []);
  useEffect(() => { charger(); }, [charger]);

  const branches = useMemo(() => (d?.branches || []).filter((b) => d.societes.some((s) => s.branche_code === b.code)), [d]);
  const branche = branches.some((b) => b.code === params.get('branche')) ? params.get('branche') : branches[0]?.code;
  const societes = useMemo(() => (d?.societes || []).filter((s) => s.branche_code === branche), [d, branche]);
  // Valeurs proposées : celles déjà saisies dans la branche.
  const proposees = (k) => [...new Set(societes.map((s) => s[k]).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'fr'));
  const synthese = useMemo(() => {
    const m = new Map();
    societes.forEach((s) => m.set(s.plaque || '', (m.get(s.plaque || '') || 0) + 1));
    return [...m.entries()].sort(([a], [b]) => (a ? (b ? a.localeCompare(b, 'fr') : -1) : 1));
  }, [societes]);

  const courant = (s) => brouillons[s.code] || { master: s.master || '', plaque: s.plaque || '' };
  const modifie = (s, k) => (courant(s)[k] || '').trim() !== (s[k] || '');
  const saisir = (s, k, v) => { setMessage(''); setBrouillons({ ...brouillons, [s.code]: { ...courant(s), [k]: v } }); };
  const annuler = (s) => { const b = { ...brouillons }; delete b[s.code]; setBrouillons(b); };
  const enregistrer = async (s) => {
    setAttente(s.code); setErreur('');
    try {
      await api.enregistrer({ code: s.code, ...courant(s) });
      annuler(s); setMessage(`${s.nom} enregistrée.`); await charger();
    } catch (e) { setErreur(e.response?.data?.error || 'Enregistrement impossible'); }
    finally { setAttente(null); }
  };

  return (
    <main className="ob-page">
      <div className="ob-head">
        <h1>Sociétés — regroupements</h1>
        <p className="ob-sub">Concession groupée et plaque de chaque société. Ce sont eux qui forment les regroupements du panneau de filtres et du sélecteur de concession, dans tous les rapports. Libellés libres : une même orthographe = un même groupe ; vide = la société n'est regroupée avec personne.</p>
      </div>
      {erreur && <div className="ob-erreur">⚠️ {erreur}</div>}
      {message && <div className="ob-ok">{message}</div>}
      {!d ? (!erreur && <div className="vt-skel" />) : (
        <>
          <div className="vt-card">
            <div className="vt-card-h">
              <h2>Sociétés</h2>
              <div className="st-tools">
                <div className="st-seg" role="group" aria-label="Branche">
                  {branches.map((b) => (
                    <button key={b.code} type="button" className={b.code === branche ? 'on' : ''}
                      onClick={() => { setParams({ branche: b.code }, { replace: true }); setMessage(''); }}>{b.nom}</button>
                  ))}
                </div>
              </div>
            </div>
            <div className="vt-card-b">
              <div className="as-synthese">
                <span>Plaques :</span>
                {synthese.map(([p, n]) => <span key={p}>{p ? <b>{p}</b> : <span className="as-vide">sans plaque</span>} {n}</span>)}
              </div>
            </div>
            <div className="vt-card-b vt-tbl-wrap">
              <datalist id="as-masters">{proposees('master').map((v) => <option key={v} value={v} />)}</datalist>
              <datalist id="as-plaques">{proposees('plaque').map((v) => <option key={v} value={v} />)}</datalist>
              <table className="vt-tbl">
                <thead><tr><th className="l">Société</th><th className="l">Code compta</th><th className="l">Concession groupée</th><th className="l">Plaque</th><th /></tr></thead>
                <tbody>
                  {societes.map((s) => {
                    const c = courant(s); const sale = modifie(s, 'master') || modifie(s, 'plaque');
                    const touche = (e) => { if (e.key === 'Enter' && sale) enregistrer(s); if (e.key === 'Escape') annuler(s); };
                    return (
                      <tr key={s.code}>
                        <td className="l"><b>{s.nom}</b></td>
                        <td className="l as-codes">{(s.codes_source || []).join(', ')}</td>
                        <td className="l"><input className={`as-input${modifie(s, 'master') ? ' modifie' : ''}`} list="as-masters" value={c.master}
                          placeholder="aucune" maxLength={50} onChange={(e) => saisir(s, 'master', e.target.value)} onKeyDown={touche} /></td>
                        <td className="l"><input className={`as-input${modifie(s, 'plaque') ? ' modifie' : ''}`} list="as-plaques" value={c.plaque}
                          placeholder="aucune" maxLength={60} onChange={(e) => saisir(s, 'plaque', e.target.value)} onKeyDown={touche} /></td>
                        <td className="as-actions">{sale && (
                          <>
                            <button type="button" className="cr-btn" disabled={attente === s.code} onClick={() => enregistrer(s)}>Enregistrer</button>
                            <button type="button" className="cr-btn ghost" disabled={attente === s.code} onClick={() => annuler(s)}>Annuler</button>
                          </>
                        )}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="vt-card">
            <div className="vt-card-h"><h2>Historique</h2><span className="hint">30 dernières modifications, toutes branches</span></div>
            <div className="vt-card-b vt-tbl-wrap">
              {!d.historique.length ? <p className="hint">Aucune modification depuis l'ouverture de l'écran.</p> : (
                <table className="vt-tbl tbl-dense">
                  <thead><tr><th className="l">Le</th><th className="l">Société</th><th className="l">Concession groupée</th><th className="l">Plaque</th><th className="l">Par</th></tr></thead>
                  <tbody>
                    {d.historique.map((h, i) => {
                      const ch = (k) => ((h.avant?.[k] || null) === (h.apres?.[k] || null) ? val(h.apres?.[k]) : <>{val(h.avant?.[k])} → <b>{val(h.apres?.[k])}</b></>);
                      return <tr key={i}><td className="l">{date(h.revise_le)}</td><td className="l">{h.nom || h.code}</td><td className="l">{ch('master')}</td><td className="l">{ch('plaque')}</td><td className="l">{h.revise_par}</td></tr>;
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </main>
  );
}
